import { useMutation, useQueryClient } from '@tanstack/react-query';

import { activateProduct } from '#/api/crmMgmt/product/activate';
import { deactivateProduct } from '#/api/crmMgmt/product/deactivate';
import {
  getProductApiErrorMessage,
  getProductInUseDetails,
  isProductVersionConflict,
} from '#/modules/crmMgmt/product/helper/apiError';
import {
  productDetailQueryKey,
  productListQueryKeyPrefix,
} from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

/** 與 SideMsg／Detail CommandBar 文案一致。 */
export const PRODUCT_ACTIVATE_CONFIRM = {
  title: '啟用產品',
  message: '確定啟用此產品？啟用後可被新客戶訂單選用。',
  confirmText: '啟用',
};

export const PRODUCT_DEACTIVATE_CONFIRM = {
  title: '停用產品',
  message: '確定停用此產品？停用後仍可讀取，但不可被新客戶訂單選用。',
  confirmText: '停用',
};

/**
 * 產品啟用／停用 mutation（confirm 由呼叫端處理）。
 *
 * @param {object} options
 * @param {string} options.id
 * @param {() => string|null|undefined} options.getUpdatedAt
 * @param {() => void|Promise<void>} [options.onAfterSuccess]
 * @param {() => void|Promise<void>} [options.onVersionConflict]
 * @param {(details: ReturnType<typeof getProductInUseDetails>) => void} [options.onInUse]
 *   停用被 409 `CRM_PRODUCT_IN_USE` 擋下時改呼叫此 callback（不再 toast）。
 */
function useProductStatusMutation({
  id,
  getUpdatedAt,
  onAfterSuccess,
  onVersionConflict,
  onInUse,
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ action }) => {
      const payload = { updatedAt: getUpdatedAt?.() };
      if (action === 'activate') {
        return activateProduct(id, payload);
      }
      if (action === 'deactivate') {
        return deactivateProduct(id, payload);
      }
      throw new Error(`Unknown action: ${action}`);
    },
    onSuccess: async (result) => {
      toast.success(result?.message || '操作成功');
      await queryClient.invalidateQueries({ queryKey: productListQueryKeyPrefix() });
      if (id) {
        await queryClient.invalidateQueries({ queryKey: productDetailQueryKey(id) });
      }
      await onAfterSuccess?.(result);
    },
    onError: (error) => {
      const inUse = getProductInUseDetails(error);
      if (inUse && onInUse) {
        onInUse(inUse);
        return;
      }
      toast.error(getProductApiErrorMessage(error));
      if (isProductVersionConflict(error)) {
        onVersionConflict?.(error);
      }
    },
  });
}

export default useProductStatusMutation;
